import { useEffect, useState } from "react";
import { AlertTriangle, CheckCircle2, Send } from "lucide-react";
import { api } from "../../api";
import { trackEvent } from "../../analytics";

const CLASS_LABEL = {
  small: { label: "Small spill", color: "#1f6a4f", bg: "#e2f0ea" },
  drum: { label: "Mid-scale (20–500 L)", color: "#8a5a13", bg: "#fdf1de" },
  bulk: { label: "Bulk / emergency", color: "#a8341f", bg: "#fbeeeb" },
};

const CHEM_OPTIONS = [
  { key: "hcl", name: "Hydrochloric Acid" },
  { key: "h2so4", name: "Sulfuric Acid" },
  { key: "naoh", name: "Caustic Soda" },
  { key: "na2co3", name: "Soda Ash" },
  { key: "hno3", name: "Nitric Acid (Weak)" },
  { key: "ch2o", name: "Formaldehyde" },
];

export default function Spill({ dark, activeChem }) {
  const [volume, setVolume] = useState("50");
  const [chem, setChem] = useState(activeChem || "hcl");
  const [plan, setPlan] = useState(null);

  useEffect(() => setChem(activeChem || "hcl"), [activeChem]);

  useEffect(() => {
    const v = parseFloat(volume);
    if (isNaN(v) || v < 0) { setPlan(null); return; }
    api.getSpillPlan(v).then(setPlan).catch(() => setPlan(null));
    trackEvent("spill_planner_used", { volume_l: v });
  }, [volume]);

  const [logForm, setLogForm] = useState({ location: "", action_taken: "", reporter_name: "", reporter_email: "", ehs_notified: false });
  const [logStatus, setLogStatus] = useState("idle");
  const [logRef, setLogRef] = useState("");

  async function submitLog(e) {
    e.preventDefault();
    if (!plan) return;
    setLogStatus("sending");
    try {
      const payload = {
        volume_l: parseFloat(volume),
        classification: plan.classification,
        chemical: chem || null,
        location: logForm.location,
        action_taken: logForm.action_taken,
        reporter_name: logForm.reporter_name,
        reporter_email: logForm.reporter_email || null,
        ehs_notified: !!logForm.ehs_notified,
      };
      const res = await api.logIncident(payload);
      setLogRef(res.reference || "");
      setLogStatus("logged");
    } catch (err) {
      setLogStatus("error");
    }
  }

  const cls = plan ? CLASS_LABEL[plan.classification] : null;

  return (
    <section className={"panel" + (dark ? " dark" : "")} id="spill" data-testid="section-spill">
      <div className="wrap">
        <div className="panel-head">
          <div className="eyebrow">Tool · Spill Response Planner</div>
          <h2>Enter volume, get a plan</h2>
          <p>Type the estimated spill volume in litres. The planner classifies size, sizes the
             containment radius, and produces a scaled response with drain-standoff warnings.</p>
        </div>

        <div className="spill-planner" data-testid="spill-planner">
          <div className="spill-input-grid">
            <div>
              <label className="mono">Volume (L)</label>
              <input
                type="number"
                min="0"
                step="1"
                value={volume}
                onChange={(e) => setVolume(e.target.value)}
                data-testid="spill-volume-input"
                className="spill-volume-input"
              />
            </div>
            <div>
              <label className="mono">Chemical (optional)</label>
              <select value={chem} onChange={(e) => setChem(e.target.value)} data-testid="spill-chem-select">
                <option value="">—</option>
                {CHEM_OPTIONS.map((c) => <option key={c.key} value={c.key}>{c.name}</option>)}
              </select>
            </div>
          </div>

          {plan && (
            <div className="spill-plan-card" data-testid="spill-plan-card">
              <div className="spill-plan-head">
                <span className="spill-badge" style={{ background: cls.bg, color: cls.color }}>{cls.label}</span>
                {plan.response_team_required && (
                  <span className="spill-badge alert"><AlertTriangle size={13} /> Response team required</span>
                )}
              </div>
              <div className="spill-metrics">
                <div><span className="mono spill-metric-label">Containment radius</span><b>{plan.containment_radius_m} m</b></div>
                <div><span className="mono spill-metric-label">Drain standoff</span><b>≥ {plan.drain_standoff_m} m</b></div>
                <div><span className="mono spill-metric-label">Volume</span><b>{plan.volume_l} L</b></div>
              </div>
              <ol className="step-list">
                {plan.steps.map((s, i) => <li key={i}>{s}</li>)}
              </ol>
            </div>
          )}
        </div>

        <div className="sub-panel-head">
          <div className="eyebrow">Tool · Incident log</div>
          <h3>Log &amp; notify EHS</h3>
          <p className="note">Fill this after containment. Timestamped and stored — pull a filtered
             list at <span className="mono">/api/incident-logs</span> for the monthly EHS review.</p>
        </div>

        {logStatus === "logged" ? (
          <div className="incident-success" data-testid="incident-success">
            <CheckCircle2 size={20} />
            <div>
              <div className="eyebrow">Reference {logRef}</div>
              <div>Incident logged. EHS will see this in the daily digest.</div>
            </div>
            <button className="btn-link" onClick={() => { setLogStatus("idle"); setLogRef(""); setLogForm({ location: "", action_taken: "", reporter_name: "", reporter_email: "", ehs_notified: false }); }} data-testid="incident-new">
              Log another
            </button>
          </div>
        ) : (
          <form className="incident-form" onSubmit={submitLog} data-testid="incident-form">
            <div className="modal-row">
              <label>
                Location
                <input required value={logForm.location} onChange={(e) => setLogForm((f) => ({ ...f, location: e.target.value }))} placeholder="e.g. Line 3, Dosing bay B" data-testid="incident-location" />
              </label>
              <label>
                Reporter name
                <input required value={logForm.reporter_name} onChange={(e) => setLogForm((f) => ({ ...f, reporter_name: e.target.value }))} data-testid="incident-reporter" />
              </label>
            </div>
            <label>
              Action taken
              <textarea rows={3} required value={logForm.action_taken} onChange={(e) => setLogForm((f) => ({ ...f, action_taken: e.target.value }))} placeholder="e.g. Contained with sock, blocked drain, notified supervisor at 14:22" data-testid="incident-action" />
            </label>
            <div className="modal-row">
              <label>
                Reporter email (optional)
                <input type="email" value={logForm.reporter_email} onChange={(e) => setLogForm((f) => ({ ...f, reporter_email: e.target.value }))} data-testid="incident-email" />
              </label>
              <label className="incident-check">
                <input type="checkbox" checked={logForm.ehs_notified} onChange={(e) => setLogForm((f) => ({ ...f, ehs_notified: e.target.checked }))} data-testid="incident-ehs" />
                <span>EHS notified on shift radio</span>
              </label>
            </div>
            {logStatus === "error" && <p className="modal-error">Failed to log — try again.</p>}
            <button className="btn btn-brand" type="submit" disabled={!plan || logStatus === "sending"} data-testid="incident-submit">
              <Send size={14} /> {logStatus === "sending" ? "Logging…" : "Log incident"}
            </button>
          </form>
        )}
      </div>
    </section>
  );
}
